<template>
  <div class="study-effectiveness">
    <!-- 第一行：全年赛事图 - 单独一行 -->
    <el-card class="chart-card full-width-card">
      <div slot="header" class="card-header">
        <span>全年赛事图</span>
        <div class="operations">
          <el-button-group class="heatmap-mode-switch">
            <el-button
              size="small"
              :type="heatmapMode === 'difficulty' ? 'primary' : ''"
              @click="heatmapMode = 'difficulty'"
            >
              难度
            </el-button>
            <el-button
              size="small"
              :type="heatmapMode === 'count' ? 'primary' : ''"
              @click="heatmapMode = 'count'"
            >
              数量
            </el-button>
          </el-button-group>
          <el-select
            v-model="selectedYear"
            size="small"
            class="year-select"
            @change="handleYearChange"
          >
            <el-option
              v-for="year in yearOptions"
              :key="year"
              :label="year + '年'"
              :value="year"
            />
          </el-select>
          <el-tooltip content="导出图片" placement="top">
            <el-button type="text" icon="el-icon-download" @click="handleExportPlan" />
          </el-tooltip>
          <el-tooltip content="放大显示" placement="top">
            <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreenPlan" />
          </el-tooltip>
        </div>
      </div>
      <div class="year-progress-section">
        <div class="annual-progress">
          <div class="progress-header">
            <span class="progress-title">📅 {{ selectedYear }} 年度进度</span>
          </div>
          <div class="progress-bar-container">
            <div class="progress-bar" :style="{ width: yearProgress + '%' }">
              <span class="progress-value">{{ yearProgress }}%</span>
            </div>
          </div>
          <div class="progress-labels">
            <span class="label-start">年初</span>
            <span class="label-end">年末</span>
          </div>
        </div>
        <div class="progress-divider"></div>
        <div class="statistics-cards">
          <div class="stat-card stat-card-total">
            <div class="stat-icon">🏆</div>
            <div class="stat-content">
              <div class="stat-number">{{ chartData.correction.total_games }}</div>
              <div class="stat-label">累计完成赛事</div>
            </div>
          </div>
          <div class="stat-card stat-card-year">
            <div class="stat-icon">🥇</div>
            <div class="stat-content">
              <div class="stat-number">{{ Math.round(chartData.plan.matrix.reduce((sum, item) => sum + item[2], 0)) }}</div>
              <div class="stat-label">今年完成赛事</div>
            </div>
          </div>
        </div>
      </div>
      <div class="matrix-chart-export-wrapper" ref="planChartExportWrapper">
        <div ref="planChart" class="chart-container matrix-chart-container"></div>
        <div class="matrix-legends">
          <div
            class="legend-item"
            v-for="item in matrixLegendItems"
            :key="`main-${item.label}`"
          >
            <span
              v-if="item.type === 'color'"
              class="legend-square"
              :style="{ backgroundColor: item.color }"
            ></span>
            <span v-else class="legend-icon">✔️</span>
            <span class="legend-label">{{ item.label }}</span>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 第二行：数据概览、计划概览、能力雷达 -->
    <div class="chart-row">
    <el-card class="chart-card">
      <div slot="header" class="card-header">
        <span>数据概览</span>
        <div class="operations">
          <el-tooltip content="导出图片" placement="top">
            <el-button type="text" icon="el-icon-download" @click="handleExportCorrection" />
          </el-tooltip>
          <el-tooltip content="放大显示" placement="top">
            <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreenCorrection" />
          </el-tooltip>
        </div>
      </div>
      <div class="data-statistics">
        <div class="statistic-row">
        <div class="statistic-item">
          <div class="statistic-label">赛事</div>
          <div class="statistic-value">{{ chartData.correction.total_games }}</div>
        </div>
        <div class="statistic-item">
          <div class="statistic-label">题目</div>
          <div class="statistic-value">{{ chartData.correction.total }}</div>
        </div>
        </div>
        <div class="statistic-row">
        <div class="statistic-item">
            <div class="statistic-label">AC</div>
            <div class="statistic-value">{{ chartData.correction.ac_questions }}</div>
          </div>
          <div class="statistic-item">
            <div class="statistic-label">订正</div>
          <div class="statistic-value">
              <span class="highlight">{{ chartData.correction.corrected }}</span>
            <span class="separator">/</span>
              <span>{{ chartData.correction.need_correction }}</span>
            </div>
          </div>
          <div class="statistic-item">
            <div class="statistic-label">挂起</div>
            <div class="statistic-value">{{ chartData.correction.pending_questions }}</div>
          </div>
        </div>
      </div>
      <div ref="correctionChart" class="chart-container"></div>
    </el-card>

    <el-card class="chart-card">
      <div slot="header" class="card-header">
        <span>计划概览</span>
        <div class="operations">
          <el-tooltip content="导出图片" placement="top">
            <el-button type="text" icon="el-icon-download" @click="handleExportPlanOverview" />
          </el-tooltip>
          <el-tooltip content="放大显示" placement="top">
            <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreenPlanOverview" />
          </el-tooltip>
        </div>
      </div>
      <div class="data-statistics">
        <div class="statistic-row">
          <div class="statistic-item">
            <div class="statistic-label">计划数</div>
            <div class="statistic-value">{{ chartData.plan.total }}</div>
          </div>
          <div class="statistic-item">
            <div class="statistic-label">已完成</div>
            <div class="statistic-value">{{ chartData.plan.completed }}</div>
          </div>
        </div>
        <div class="statistic-row">
          <div class="statistic-item">
            <div class="statistic-label">进行中</div>
            <div class="statistic-value">{{ chartData.plan.in_progress }}</div>
          </div>
          <div class="statistic-item">
            <div class="statistic-label">已暂停</div>
            <div class="statistic-value">{{ chartData.plan.paused }}</div>
          </div>
        </div>
      </div>
      <div ref="planOverviewChart" class="chart-container"></div>
    </el-card>

    <el-card class="chart-card">
      <div slot="header" class="card-header">
        <span>能力雷达</span>
        <div class="operations">
          <el-tooltip content="导出图片" placement="top">
            <el-button type="text" icon="el-icon-download" @click="handleExportRadar" />
          </el-tooltip>
          <el-tooltip content="放大显示" placement="top">
            <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreenRadar" />
          </el-tooltip>
        </div>
      </div>
      <div ref="radarChart" class="chart-container radar-chart-container"></div>
    </el-card>
    </div>

    <!-- 全屏弹窗 -->
    <el-dialog
      :visible.sync="dialogVisible"
      :append-to-body="true"
      :modal-append-to-body="false"
      :close-on-click-modal="false"
      width="90%"
      :destroy-on-close="false"
      :show-close="false"
      class="chart-dialog"
      @closed="handleDialogClosed"
      @open="handleDialogOpen"
      @close="handleDialogClose"
    >
      <div slot="title" class="dialog-header">
        <span>{{ dialogTitle }}</span>
        <div class="dialog-operations">
          <el-tooltip content="导出图片" placement="top">
            <el-button type="text" icon="el-icon-download" @click="handleExportFullscreen" />
          </el-tooltip>
          <el-tooltip content="关闭" placement="top">
            <el-button type="text" icon="el-icon-close" @click="closeDialog" />
          </el-tooltip>
        </div>
      </div>
      <div v-if="currentChartType === 'plan'" class="fullscreen-plan-wrapper" ref="fullscreenPlanWrapper">
        <div class="fullscreen-progress-section">
          <div class="annual-progress">
            <div class="progress-header">
              <span class="progress-title">📅 {{ selectedYear }} 年度进度</span>
            </div>
            <div class="progress-bar-container">
              <div class="progress-bar" :style="{ width: yearProgress + '%' }">
                <span class="progress-value">{{ yearProgress }}%</span>
              </div>
            </div>
            <div class="progress-labels">
              <span class="label-start">年初</span>
              <span class="label-end">年末</span>
            </div>
          </div>
          <div class="progress-divider"></div>
          <div class="fullscreen-statistics-cards">
            <div class="stat-card stat-card-total">
              <div class="stat-icon">🏆</div>
              <div class="stat-content">
                <div class="stat-number">{{ chartData.correction.total_games }}</div>
                <div class="stat-label">累计完成赛事</div>
              </div>
            </div>
            <div class="stat-card stat-card-year">
              <div class="stat-icon">🥇</div>
              <div class="stat-content">
                <div class="stat-number">{{ Math.round(chartData.plan.matrix.reduce((sum, item) => sum + item[2], 0)) }}</div>
                <div class="stat-label">今年完成赛事</div>
              </div>
            </div>
          </div>
        </div>
        <div class="fullscreen-chart" ref="fullscreenChart"></div>
        <div class="matrix-legends fullscreen">
          <div
            class="legend-item"
            v-for="item in matrixLegendItems"
            :key="`fullscreen-${item.label}`"
          >
            <span
              v-if="item.type === 'color'"
              class="legend-square"
              :style="{ backgroundColor: item.color }"
            ></span>
            <span v-else class="legend-icon">✔️</span>
            <span class="legend-label">{{ item.label }}</span>
          </div>
        </div>
      </div>
      <div v-else class="fullscreen-chart" ref="fullscreenChart"></div>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import request from '@/utils/request'
import _ from 'lodash'
import html2canvas from 'html2canvas'

export default {
  name: 'StudyEffectiveness',
  data() {
    return {
      correctionChart: null,
      planChart: null,
      planOverviewChart: null,
      radarChart: null,
      chartData: {
        correction: {
          rate: 0,
          total: 0,      // 总题目数
          total_games: 0,  // 赛事总数
          completed: 0,   // 已订正数
          remaining: 0,    // 未订正数
          corrected: 0,
          need_correction: 0,
          ac_questions: 0,
          pending_questions: 0
        },
        plan: {
          rate: 0,
          total: 0,      // 总计划数
          completed: 0,   // 已完成数
          in_progress: 0,  // 进行中数
          paused: 0,     // 已暂停数
          matrix: [],     // 矩阵数据
          highlightDates: [] // 需要高亮的日期
        },
        ability: {
          all: [],
          recent: []
        }
      },
      matrixColorPalette: {
        zero: '#E0E4EB',
        one: '#B9F0B0',
        two: '#A7D3FF',
        threePlus: '#CDB6FF',
        future: '#CCCCCC'
      },
      planDailyMeta: {
        weeksInYear: 0,
        daysInYear: 0
      },
      fullscreenChart: null,
      dialogVisible: false,
      dialogTitle: '',
      currentChartType: '',
      resizeHandler: null,
      isInitializing: false,
      selectedYear: new Date().getFullYear(),
      yearOptions: [],
      heatmapMode: 'difficulty' // 'difficulty' 或 'count'
    }
  },
  computed: {
    currentYear() {
      return new Date().getFullYear()
    },
    yearProgress() {
      const now = new Date()
      const currentYear = now.getFullYear()
      
      // 如果是历史年份，进度为100%
      if (this.selectedYear < currentYear) {
        return 100
      }
      
      // 如果是未来年份，进度为0%
      if (this.selectedYear > currentYear) {
        return 0
      }
      
      // 当前年份，计算实际进度
      const start = new Date(currentYear, 0, 1)
      const diff = now - start
      const oneYear = 1000 * 60 * 60 * 24 * 365
      return Math.min(Math.round((diff / oneYear * 100) * 100) / 100, 100)
    },
    matrixLegendPieces() {
      const palette = this.matrixColorPalette || {}
      if (this.heatmapMode === 'difficulty') {
        return [
          { label: '无赛事', color: palette.noGame || '#E0E4EB' },
          { label: '未定义', color: palette.undefined || '#B8BCC8' },
          { label: '入门', color: palette.red || '#FF9999' },
          { label: '普及-', color: palette.orange || '#FFB366' },
          { label: '普及/提高-', color: palette.yellow || '#FFD966' },
          { label: '普及+/提高', color: palette.green || '#A8D5A8' },
          { label: '提高+/省选-', color: palette.blue || '#99CCFF' },
          { label: '省选/NOI-', color: palette.purple || '#CC99FF' },
          { label: 'NOI/NOI+/CTSC', color: palette.black || '#666699' }
        ]
      } else {
        return [
          { label: '无赛事', color: palette.noGame || '#E0E4EB' },
          { label: '0', color: palette.zero || '#E0E4EB' },
          { label: '1', color: palette.one || '#B9F0B0' },
          { label: '2', color: palette.two || '#A7D3FF' },
          { label: '≥3', color: palette.threePlus || '#CDB6FF' }
        ]
      }
    },
    matrixLegendItems() {
      const colorItems = this.matrixLegendPieces.map(piece => ({
        ...piece,
        type: 'color'
      }))
      colorItems.push({
        label: '已订正',
        type: 'check'
      })
      return colorItems
    }
  },
  watch: {
    dialogVisible(val) {
      if (!val && this.fullscreenChart) {
        this.fullscreenChart.dispose()
        this.fullscreenChart = null
      }
    },
    heatmapMode() {
      // 当切换模式时，重新更新图表
      if (this.planChart) {
        this.updateCharts()
      }
    }
  },
  mounted() {
    this.initCharts()
    this.fetchData()
    window.addEventListener('resize', this.resizeCharts)
    // 修改年份选择范围，从2020年到当前年份
    const currentYear = new Date().getFullYear()
    this.yearOptions = Array.from(
      {length: currentYear - 2022}, 
      (_, i) => currentYear - i
    )
  },
  beforeDestroy() {
    if (this.correctionChart) {
      this.correctionChart.dispose()
    }
    if (this.planChart) {
      this.planChart.dispose()
    }
    if (this.planOverviewChart) {
      this.planOverviewChart.dispose()
    }
    if (this.radarChart) {
      this.radarChart.dispose()
    }
    if (this.fullscreenChart) {
      this.fullscreenChart.dispose()
    }
    window.removeEventListener('resize', this.resizeCharts)
  },
  methods: {
    createStripePattern() {
      // 创建一个斜纹图案的 Canvas
      const canvas = document.createElement('canvas')
      const size = 8
      canvas.width = size
      canvas.height = size
      const ctx = canvas.getContext('2d')
      
      // 设置背景色（与时间未到的颜色一致）
      ctx.fillStyle = '#CCCCCC'
      ctx.fillRect(0, 0, size, size)
      
      // 绘制斜纹线条
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)'
      ctx.lineWidth = 1
      ctx.beginPath()
      // 从左上到右下的斜线
      ctx.moveTo(0, 0)
      ctx.lineTo(size, size)
      ctx.moveTo(-size, 0)
      ctx.lineTo(0, size)
      ctx.stroke()
      
      return canvas.toDataURL()
    },
    initCharts() {
      this.correctionChart = echarts.init(this.$refs.correctionChart)
      this.planOverviewChart = echarts.init(this.$refs.planOverviewChart)
      this.radarChart = echarts.init(this.$refs.radarChart)
      // planChart 初始化 - 使用 $nextTick 确保 DOM 已渲染
      this.$nextTick(() => {
        if (this.$refs.planChart) {
          try {
            this.planChart = echarts.init(this.$refs.planChart)
            console.log('mounted时初始化planChart成功，容器高度:', this.$refs.planChart.offsetHeight)
          } catch (error) {
            console.error('mounted时初始化planChart失败:', error)
          }
        } else {
          console.warn('mounted时planChart ref不存在')
        }
      })
    },
    resizeCharts() {
      this.correctionChart?.resize()
      this.planOverviewChart?.resize()
      this.radarChart?.resize()
      // 热力图resize和grid更新
      if (this.planChart && this.$refs.planChart) {
        const container = this.$refs.planChart
        if (container && (container.offsetHeight > 0 || container.clientHeight > 0)) {
          this.planChart.resize()
          this.$nextTick(() => {
            this.updatePlanChartGrid()
          })
        }
      }
    },
    async fetchData() {
      try {
        console.log('开始获取数据...')
        
        const correctionPromise = request({
          url: '/api/analysis/question/correction',
          method: 'get'
        })
        const planCountPromise = request({
          url: '/api/analysis/plan/count',
          method: 'get'
        })
        const dailyMatrixPromise = request({
          url: '/api/analysis/game/daily-matrix',
          method: 'get',
          params: { year: this.selectedYear }
        })
        const abilityPromise = request({
          url: '/api/analysis/ability/radar',
          method: 'get'
        })

        const [
          correctionResult,
          planCountResult,
          dailyMatrixResult,
          abilityResult
        ] = await Promise.allSettled([
          correctionPromise,
          planCountPromise,
          dailyMatrixPromise,
          abilityPromise
        ])

        if (correctionResult.status === 'fulfilled') {
          const correctionRes = correctionResult.value
          console.log('订正数据响应:', correctionRes)
          const correctionData = correctionRes.data || {}
          this.chartData.correction = {
            rate: correctionData.rate || 0,
            total: correctionData.total || 0,
            total_games: correctionData.total_games || 0,
            corrected: correctionData.corrected || 0,
            need_correction: correctionData.need_correction || 0,
            ac_questions: correctionData.ac_questions || 0,
            pending_questions: correctionData.pending_questions || 0
          }
        } else {
          console.error('获取订正数据失败:', correctionResult.reason)
          this.$message.error('获取订正数据失败')
        }

        if (planCountResult.status === 'fulfilled') {
          const planCountRes = planCountResult.value
          console.log('计划数据响应:', planCountRes)
          const planCountData = planCountRes.data || {}
          const validTotalPlans = (planCountData.total || 0) - (planCountData.terminated || 0)
          this.chartData.plan = {
            ...this.chartData.plan,
            total: validTotalPlans,
            completed: planCountData.completed || 0,
            in_progress: planCountData.in_progress || 0,
            paused: planCountData.paused || 0,
            rate: validTotalPlans ? (planCountData.completed / validTotalPlans * 100) : 0
          }
        } else {
          console.error('获取计划数据失败:', planCountResult.reason)
          this.$message.error('获取计划数据失败')
        }

        if (dailyMatrixResult.status === 'fulfilled') {
          const dailyMatrixRes = dailyMatrixResult.value
          console.log('赛事每日矩阵数据响应:', dailyMatrixRes)
          const dailyMatrixData = dailyMatrixRes.data || {}
          const dailyList = dailyMatrixData.data || []
          const dailyMatrix = dailyList.map(item => {
            const weekIndex = Number(item.week_index ?? 0)
            const dayOfWeek = Number(item.day_of_week ?? 0)
            const value = Number(item.count ?? 0)
            const dateStr = item.date || ''
            const isFuture = Number(item.is_future ?? 0)
            const games = item.games || []
            const isAllCorrected = games.length > 0 && games.every(game => game.is_all_corrected === true)
            const avgDifficulty = item.avg_difficulty ?? null  // 平均难度百分比
            return [weekIndex, dayOfWeek, value, dateStr, isFuture, games, isAllCorrected, avgDifficulty]
          })
          console.log('处理后的每日矩阵数据:', dailyMatrix)
          this.chartData.plan.matrix = dailyMatrix
          this.chartData.plan.highlightDates = dailyMatrixData.highlight_dates || []
          this.planDailyMeta = {
            weeksInYear: dailyMatrixData.weeks_in_year || Math.ceil(((dailyMatrixData.days_in_year || 365) + 6) / 7),
            daysInYear: dailyMatrixData.days_in_year || 365
          }
        } else {
          console.error('获取赛事每日矩阵数据失败:', dailyMatrixResult.reason)
          this.$message.error('获取赛事每日矩阵数据失败')
        }

        if (abilityResult.status === 'fulfilled') {
          const abilityRes = abilityResult.value
          console.log('能力数据响应:', abilityRes)
          const abilityData = abilityRes.data || {}
          this.chartData.ability = {
            all: abilityData.all_indicators || [],
            recent: abilityData.recent_indicators || []
          }
        } else {
          console.error('获取能力数据失败:', abilityResult.reason)
          this.$message.error('获取能力数据失败')
        }

        console.log('更新图表...')
        this.updateCharts()
      } catch (error) {
        console.error('数据处理失败:', error)
        this.$message.error('数据处理失败，请检查控制台日志')
      }
    },
    updateCharts() {
      // 数据概览图表 - 只保留订正率
      const correctionOption = {
        backgroundColor: '#ffffff',
        tooltip: {
          trigger: 'item',
          axisPointer: {
            type: 'none'
          },
          backgroundColor: 'rgba(50, 50, 50, 0.9)',
          borderColor: '#333',
          textStyle: {
            color: '#fff',
            fontSize: 12
          },
          formatter: function(params) {
            const chartData = this.chartData
            // 订正率相关的提示
            if (params.seriesName === 'AC部分' || params.seriesName === '订正率') {
              const acRate = (chartData.correction.ac_questions / chartData.correction.total * 100).toFixed(1)
              const correctionRate = chartData.correction.rate.toFixed(1)
              const needCorrectionRate = (chartData.correction.need_correction / chartData.correction.total * 100).toFixed(1)
              return `
                <div style="padding: 4px;">
                  <div style="font-weight: bold; margin-bottom: 6px;">数据概览</div>
                  <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                    <div>AC率：${acRate}%</div>
                    <div>订正率：${correctionRate}%（需订正题目占比：${needCorrectionRate}%）</div>
                    <div>总题目数：${chartData.correction.total}</div>
                    <div>已订正：${chartData.correction.corrected}</div>
                    <div>需订正：${chartData.correction.need_correction}</div>
                    <div>已挂起：${chartData.correction.pending_questions}</div>
                  </div>
                </div>
              `
            }
            return ''
          }.bind(this)
        },
        legend: {
          show: true,
          bottom: 2,
          left: 'center',
          itemGap: 20,
          selectedMode: true,
          textStyle: {
            color: '#666666',
            fontSize: 12
          },
          itemStyle: {
            borderWidth: 0
          },
          inactiveColor: '#999999',
          inactiveBorderColor: 'transparent',
          data: [
            {
              name: '订正率',
              itemStyle: {
                color: '#409EFF'
              }
            }
          ]
        },
        grid: {
          top: '5%',
          bottom: '15%',
          left: 20,
          right: 20,
          containLabel: true
        },
        xAxis: {
          show: true,
          type: 'value',
          max: 100,
          min: 0,
          interval: 25,
          axisLabel: {
            show: true,
            formatter: '{value}%',
            color: '#999',
            fontSize: 11
          },
          splitLine: {
            show: true,
            lineStyle: {
              type: 'dashed',
              color: '#E1E1E1'
            }
          },
          axisTick: {
            show: false
          },
          axisLine: {
            show: false
          }
        },
        yAxis: {
          type: 'category',
          data: ['订正率'],
          axisLabel: {
            show: false
          },
          axisTick: {
            show: false
          },
          axisLine: {
            show: false
          }
        },
        series: [
          {
            name: '背景',
            type: 'bar',
            barWidth: 42,
            silent: true,
            itemStyle: {
              color: '#f5f7fa',
              borderRadius: 21
            },
            data: [100]
          },
          {
            name: 'AC部分',
            type: 'bar',
            barWidth: 42,
            stack: 'correction',
            barGap: '-100%',
            data: [{
              value: (this.chartData.correction.ac_questions / this.chartData.correction.total * 100),
              itemStyle: {
                color: '#67C23A',
                borderRadius: [21, 21, 21, 21]
              },
              label: {
                show: true,
                position: 'inside',
                distance: 0,
                fontSize: 12,
                color: '#ffffff',
                fontWeight: 'bold',
                formatter: 'AC',
                padding: [0, 0, 0, 0]
              }
            }]
          },
          {
            name: '订正率',
            type: 'bar',
            barWidth: 42,
            stack: 'correction',
            data: [{
              value: (this.chartData.correction.corrected / this.chartData.correction.total * 100),
              itemStyle: {
                color: '#409EFF',
                borderRadius: [21, 21, 21, 21],
                decal: {
                  symbol: 'rect',
                  symbolSize: 3,
                  dashArrayX: [1, 0],
                  dashArrayY: [2, 4],
                  rotation: -0.785,
                  color: 'rgba(255, 255, 255, 0.3)'
                }
              },
              label: {
                show: true,
                position: 'right',
                distance: 8,
                fontSize: 12,
                color: '#409EFF',
                fontWeight: 'bold',
                formatter: () => this.chartData.correction.rate.toFixed(1) + '%',
                padding: [0, 0, 0, 0]
              }
            }]
          }
        ]
      }

      delete correctionOption.title

      this.correctionChart.setOption(correctionOption)

      // 计划概览图表
      const planOverviewOption = {
        backgroundColor: '#ffffff',
        tooltip: {
          trigger: 'item',
          axisPointer: {
            type: 'none'
          },
          backgroundColor: 'rgba(50, 50, 50, 0.9)',
          borderColor: '#333',
          textStyle: {
            color: '#fff',
            fontSize: 12
          },
          formatter: function(params) {
            const chartData = this.chartData
            if (params.seriesName === '计划-已完成' || params.seriesName === '计划-进行中' || params.seriesName === '计划-已暂停') {
              const completedRate = (chartData.plan.completed / chartData.plan.total * 100).toFixed(1)
              const inProgressRate = (chartData.plan.in_progress / chartData.plan.total * 100).toFixed(1)
              const pausedRate = (chartData.plan.paused / chartData.plan.total * 100).toFixed(1)
              return `
                <div style="padding: 4px;">
                  <div style="font-weight: bold; margin-bottom: 6px;">计划概览</div>
                  <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                    <div>已完成：${completedRate}%（${chartData.plan.completed}）</div>
                    <div>进行中：${inProgressRate}%（${chartData.plan.in_progress}）</div>
                    <div>已暂停：${pausedRate}%（${chartData.plan.paused}）</div>
                    <div>总计划数：${chartData.plan.total}</div>
                  </div>
                </div>
              `
            }
            return ''
          }.bind(this)
        },
        legend: {
          show: true,
          bottom: 2,
          left: 'center',
          itemGap: 20,
          selectedMode: true,
          textStyle: {
            color: '#666666',
            fontSize: 12
          },
          itemStyle: {
            borderWidth: 0
          },
          inactiveColor: '#999999',
          inactiveBorderColor: 'transparent',
          data: [
            {
              name: '计划-已完成',
              itemStyle: {
                color: '#E6A23C'
              }
            },
            {
              name: '计划-进行中',
              itemStyle: {
                color: '#F0C354'
              }
            },
            {
              name: '计划-已暂停',
              itemStyle: {
                color: '#909399'
              }
            }
          ]
        },
        grid: {
          top: '5%',
          bottom: '15%',
          left: 20,
          right: 20,
          containLabel: true
        },
        xAxis: {
          show: true,
          type: 'value',
          max: 100,
          min: 0,
          interval: 25,
          axisLabel: {
            show: true,
            formatter: '{value}%',
            color: '#999',
            fontSize: 11
          },
          splitLine: {
            show: true,
            lineStyle: {
              type: 'dashed',
              color: '#E1E1E1'
            }
          },
          axisTick: {
            show: false
          },
          axisLine: {
            show: false
          }
        },
        yAxis: {
          type: 'category',
          data: ['计划完成率'],
          axisLabel: {
            show: false
          },
          axisTick: {
            show: false
          },
          axisLine: {
            show: false
          }
        },
        series: [
          {
            name: '背景',
            type: 'bar',
            barWidth: 42,
            silent: true,
            itemStyle: {
              color: '#f5f7fa',
              borderRadius: 21
            },
            data: [100]
          },
          {
            name: '计划-已完成',
            type: 'bar',
            barWidth: 42,
            stack: 'plan',
            barGap: '-100%',
            data: [{
              value: (this.chartData.plan.completed / this.chartData.plan.total * 100),
              itemStyle: {
                color: '#E6A23C',
                borderRadius: [21, 21, 21, 21]
              },
              label: {
                show: true,
                position: 'inside',
                distance: 0,
                fontSize: 12,
                color: '#ffffff',
                fontWeight: 'bold',
                formatter: (params) => params.value.toFixed(1) + '%',
                padding: [0, 0, 0, 0]
              }
            }]
          },
          {
            name: '计划-进行中',
            type: 'bar',
            barWidth: 42,
            stack: 'plan',
            data: [{
              value: (this.chartData.plan.in_progress / this.chartData.plan.total * 100),
              itemStyle: {
                color: '#F0C354',
                borderRadius: [21, 21, 21, 21],
                decal: {
                  symbol: 'rect',
                  symbolSize: 3,
                  dashArrayX: [1, 0],
                  dashArrayY: [2, 4],
                  rotation: -0.785,
                  color: 'rgba(255, 255, 255, 0.3)'
                }
              },
              label: {
                show: true,
                position: 'top',
                distance: 8,
                fontSize: 12,
                color: '#F0C354',
                fontWeight: 'bold',
                formatter: (params) => params.value.toFixed(1) + '%',
                padding: [0, 0, 0, 0]
              }
            }]
          },
          {
            name: '计划-已暂停',
            type: 'bar',
            barWidth: 42,
            stack: 'plan',
            data: [{
              value: (this.chartData.plan.paused / this.chartData.plan.total * 100),
              itemStyle: {
                color: '#909399',
                borderRadius: [21, 21, 21, 21],
                decal: {
                  symbol: 'rect',
                  symbolSize: 3,
                  dashArrayX: [1, 0],
                  dashArrayY: [2, 4],
                  rotation: -0.785,
                  color: 'rgba(255, 255, 255, 0.3)'
                }
              },
              label: {
                show: true,
                position: 'bottom',
                distance: 8,
                fontSize: 12,
                color: '#909399',
                fontWeight: 'bold',
                formatter: (params) => params.value.toFixed(1) + '%',
                padding: [0, 0, 0, 0]
              }
            }]
          }
        ]
      }

      delete planOverviewOption.title

      this.planOverviewChart.setOption(planOverviewOption)

      // 能力雷达图
      const radarOption = {
        backgroundColor: '#ffffff',
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(50, 50, 50, 0.9)',
          borderColor: '#333',
          textStyle: {
            color: '#fff',
            fontSize: 12
          },
          formatter: (params) => {
            if (!params || !Array.isArray(params) || params.length === 0) return ''
            const firstParam = params[0]
            const seriesName = firstParam.seriesName || ''
            return `
              <div style="padding: 4px;">
                <div style="font-weight: bold; margin-bottom: 6px;">${seriesName}</div>
                <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                  ${params.map(param => {
                    const value = param.value || 0
                    const name = param.seriesName || ''
                    return `<div>${name}：${value.toFixed(1)}%</div>`
                  }).join('')}
                </div>
              </div>
            `
          }
        },
        legend: {
          data: ['全部赛事', '最近10场'],
          bottom: 0,
          left: 'center',
          orient: 'horizontal',
          itemGap: 30,
          padding: [0, 0, 0, 0],
          textStyle: {
            fontSize: 14,
            color: '#666'
          },
          itemWidth: 15,
          itemHeight: 10,
          itemStyle: {
            borderWidth: 0
          }
        },
        grid: {
          top: '10%',
          bottom: '15%',
          left: '10%',
          right: '10%',
          containLabel: true
        },
        radar: {
          indicator: this.chartData.ability.all.map((item, index) => {
            const recentValue = this.chartData.ability.recent[index]?.value || 0
            if (item.name === '排位') {
              return {
                name: `${item.name} Top\n{blue|${(100 - item.value).toFixed(1)}%}\n{green|${(100 - recentValue).toFixed(1)}%}`,
                max: 100
              }
            }
            return {
              name: `${item.name}\n{blue|${item.value}%}\n{green|${recentValue}%}`,
              max: 100
            }
          }) || [
            { name: '得分\n{blue|0%}\n{green|0%}', max: 100 },
            { name: '排位 Top\n{blue|0%}\n{green|0%}', max: 100 },
            { name: 'AC\n{blue|0%}\n{green|0%}', max: 100 },
            { name: '订正\n{blue|0%}\n{green|0%}', max: 100 },
            { name: '掌握\n{blue|0%}\n{green|0%}', max: 100 },
            { name: '复盘\n{blue|0%}\n{green|0%}', max: 100 }
          ],
          radius: '60%',
          center: ['50%', '45%'],
          splitNumber: 4,
          axisName: {
            color: '#666',
            fontSize: 12,
            lineHeight: 14,
            padding: [3, 0, 0, 0],
            rich: {
              blue: {
                color: '#409EFF',
                fontSize: 12,
                padding: [0, 0, 0, 0]
              },
              green: {
                color: '#67C23A',
                fontSize: 12,
                padding: [0, 0, 0, 0]
              }
            }
          },
          splitLine: {
            lineStyle: {
              color: '#E1E1E1'
            }
          },
          splitArea: {
            show: true,
            areaStyle: {
              color: ['rgba(250,250,250,0.3)', 'rgba(200,200,200,0.1)']
            }
          }
        },
        series: [{
          name: '全部赛事',
          type: 'radar',
          z: 2,
          data: [{
            value: (this.chartData.ability.all && this.chartData.ability.all.length > 0) 
              ? this.chartData.ability.all.map(item => item.value) 
              : [0, 0, 0, 0, 0, 0],
            name: '全部赛事',
            areaStyle: {
              color: 'rgba(64, 158, 255, 0.2)'
            },
            lineStyle: {
              color: '#409EFF',
              width: 2
            },
            itemStyle: {
              color: '#409EFF'
            },
            symbolSize: 6
          }]
        }, {
          name: '最近10场',
          type: 'radar',
          z: 1,
          data: [{
            value: (this.chartData.ability.recent && this.chartData.ability.recent.length > 0) 
              ? this.chartData.ability.recent.map(item => item.value) 
              : [0, 0, 0, 0, 0, 0],
            name: '最近10场',
            areaStyle: {
              color: 'rgba(103, 194, 58, 0.2)'
            },
            lineStyle: {
              color: '#67C23A',
              width: 2
            },
            itemStyle: {
              color: '#67C23A'
            },
            symbolSize: 6
          }]
        }]
      }

      this.radarChart.setOption(radarOption)

      // 全年赛事图 - 按天显示
      // 计算该年份的总天数和周数（优先使用后端返回的元数据）
      const isLeapYear = (this.selectedYear % 4 === 0 && this.selectedYear % 100 !== 0) || (this.selectedYear % 400 === 0)
      const fallbackDaysInYear = isLeapYear ? 366 : 365
      const daysInYear = this.planDailyMeta.daysInYear || fallbackDaysInYear
      const weeksInYear = this.planDailyMeta.weeksInYear || Math.ceil(daysInYear / 7)
      
      // 生成X轴标签（自然周）
      const xAxisData = []
      const startOfYear = new Date(this.selectedYear, 0, 1)
      const startWeekday = startOfYear.getDay() === 0 ? 6 : startOfYear.getDay() - 1 // 0=星期一
      for (let week = 0; week < weeksInYear; week++) {
        const startDay = week * 7 - startWeekday + 1
        if (startDay > daysInYear) {
          xAxisData.push('')
          continue
        }
        const date = new Date(this.selectedYear, 0, Math.max(1, startDay))
        if (date.getDate() <= 7 || week === 0) {
          xAxisData.push(`${date.getMonth() + 1}月`)
        } else {
          xAxisData.push('')
        }
      }
      
      // Y轴：星期一到星期日
      const yAxisData = ['星期一', '星期二', '星期三', '星期四', '星期五', '星期六', '星期日']
      
      // 难度颜色映射（适度对比度，黄色和橙色明显区分）
      const difficultyColorMap = {
        undefined: '#B8BCC8',  // 未定义 - 浅灰色（与无赛事、未来时间区分）
        red: '#FF9999',        // 入门 (0-14.29%) - 浅红色（提高对比度）
        orange: '#FFB366',     // 普及- (14.29-28.58%) - 偏红橙色（与黄色区分）
        yellow: '#FFD966',     // 普及/提高- (28.58-42.87%) - 偏绿黄色（与橙色区分）
        green: '#A8D5A8',      // 普及+/提高 (42.87-57.15%) - 浅绿色（提高对比度）
        blue: '#99CCFF',       // 提高+/省选- (57.15-71.44%) - 浅蓝色（提高对比度）
        purple: '#CC99FF',     // 省选/NOI- (71.44-85.72%) - 浅紫色（提高对比度）
        black: '#666699'       // NOI/NOI+/CTSC (85.72-100%) - 深灰蓝色（更黑，与未定义明显区分）
      }
      
      // 数量颜色映射（之前的版本）
      const countColorPalette = {
        zero: '#E0E4EB',
        one: '#B9F0B0',
        two: '#A7D3FF',
        threePlus: '#CDB6FF',
        future: '#CCCCCC'
      }
      
      // 无赛事和时间未到的灰色（与未定义区分）
      const colorPalette = {
        noGame: '#E0E4EB',     // 无赛事 - 浅灰色
        future: '#CCCCCC'      // 时间未到 - 中灰色
      }
      
      // 根据模式设置颜色调色板
      if (this.heatmapMode === 'difficulty') {
        this.matrixColorPalette = { ...difficultyColorMap, ...colorPalette }
      } else {
        this.matrixColorPalette = { ...countColorPalette, ...colorPalette }
      }

      // 根据难度百分比获取难度级别和颜色
      const getDifficultyInfo = (avgDifficultyPercentage) => {
        if (avgDifficultyPercentage === null || avgDifficultyPercentage === undefined) {
          return { level: 'undefined', color: difficultyColorMap.undefined, label: '未定义' }
        }
        const percentage = Number(avgDifficultyPercentage)
        if (isNaN(percentage)) {
          return { level: 'undefined', color: difficultyColorMap.undefined, label: '未定义' }
        }
        if (percentage <= 14.29) return { level: 'red', color: difficultyColorMap.red, label: '入门' }
        if (percentage <= 28.58) return { level: 'orange', color: difficultyColorMap.orange, label: '普及-' }
        if (percentage <= 42.87) return { level: 'yellow', color: difficultyColorMap.yellow, label: '普及/提高-' }
        if (percentage <= 57.15) return { level: 'green', color: difficultyColorMap.green, label: '普及+/提高' }
        if (percentage <= 71.44) return { level: 'blue', color: difficultyColorMap.blue, label: '提高+/省选-' }
        if (percentage <= 85.72) return { level: 'purple', color: difficultyColorMap.purple, label: '省选/NOI-' }
        return { level: 'black', color: difficultyColorMap.black, label: 'NOI/NOI+/CTSC' }
      }

      // 根据数量获取颜色（数量维度）
      const getCountColor = (count) => {
        if (count >= 3) return countColorPalette.threePlus
        if (count === 2) return countColorPalette.two
        if (count === 1) return countColorPalette.one
        return countColorPalette.zero
      }

      // 获取单元格颜色（根据模式选择）
      const getCellColor = (avgDifficultyPercentage, count, isFuture) => {
        if (isFuture) return colorPalette.future  // 未来时间
        if (count === 0) return colorPalette.noGame  // 无赛事
        
        if (this.heatmapMode === 'difficulty') {
          const difficultyInfo = getDifficultyInfo(avgDifficultyPercentage)
          return difficultyInfo.color
        } else {
          return getCountColor(count)
        }
      }

      // 获取分数颜色（根据得分率，参考通用颜色样式，适配深色背景tooltip）
      const getScoreColorByRate = (scoreRate) => {
        if (scoreRate === null || scoreRate === undefined) return '#bfbfbf' // 默认灰色（深色背景用较亮色）
        if (scoreRate >= 90) return '#73d13d' // 绿色（深色背景用较亮色）
        if (scoreRate >= 75) return '#95de64' // 黄绿色（深色背景用较亮色）
        if (scoreRate >= 50) return '#ffd666' // 黄色（深色背景用较亮色）
        if (scoreRate >= 25) return '#ffc53d' // 橙色（深色背景用较亮色）
        return '#ff7875' // 红色（深色背景用较亮色）
      }

      // 获取赛事级别标签样式（适配深色背景tooltip）
      const getGameLevelTagStyle = (gameLevel) => {
        // 根据不同的赛事级别设置不同的标签颜色（使用较亮的颜色以适配深色背景）
        const levelColorMap = {
          '省选': { bg: 'rgba(207, 19, 34, 0.2)', border: '#ff4d4f', color: '#ff7875' },
          'NOI': { bg: 'rgba(207, 19, 34, 0.2)', border: '#ff4d4f', color: '#ff7875' },
          'NOIP': { bg: 'rgba(212, 136, 6, 0.2)', border: '#faad14', color: '#ffc53d' },
          'CSP-S': { bg: 'rgba(212, 136, 6, 0.2)', border: '#faad14', color: '#ffc53d' },
          'CSP-J': { bg: 'rgba(181, 142, 5, 0.2)', border: '#d4b106', color: '#ffd666' },
          'S-提高级': { bg: 'rgba(43, 133, 228, 0.2)', border: '#40a9ff', color: '#69c0ff' },
          'J-入门级': { bg: 'rgba(43, 133, 228, 0.2)', border: '#40a9ff', color: '#69c0ff' }
        }
        
        const defaultStyle = { bg: 'rgba(144, 147, 153, 0.2)', border: '#8c8c8c', color: '#bfbfbf' }
        const style = levelColorMap[gameLevel] || defaultStyle
        
        return `display: inline-block; padding: 0px 4px; margin-right: 4px; border-radius: 2px; font-size: 11px; background-color: ${style.bg}; border: 1px solid ${style.border}; color: ${style.color}; line-height: 1.2;`
      }

      // 特殊标记：设置金色发光边框，来自后端返回的高亮日期
      const highlightGoldDates = Array.isArray(this.chartData.plan.highlightDates)
        ? this.chartData.plan.highlightDates
        : []
      const highlightGoldDateSet = new Set(highlightGoldDates)
      const goldGlowItemStyle = {
        borderColor: 'rgba(253, 214, 121, 1)',
        borderWidth: 5,
        shadowBlur: 55,
        shadowColor: 'rgba(253, 214, 121, 0.98)',
        shadowOffsetX: 0,
        shadowOffsetY: 0
      }
      const goldHighlightData = []
      const futureStripeData = []  // 收集时间未到的单元格数据

      const planOption = {
        backgroundColor: '#ffffff',
        tooltip: {
          trigger: 'item',
          confine: true,
          triggerOn: 'mousemove',
          position: 'top',
          borderWidth: 0,
          backgroundColor: 'rgba(50, 50, 50, 0.9)',
          borderColor: '#333',
          textStyle: {
            color: '#fff',
            fontSize: 12
          },
          formatter: function(params) {
            if (params && params.value) {
              const dateLabel = params.value[3] || ''
              const games = params.value[5] || []  // 赛事详细信息列表
              const avgDifficulty = params.value[7] ?? null  // 平均难度百分比
              
              // 日期作为标题
              const titleDate = `<div style="font-weight: bold; margin-bottom: 6px;">${dateLabel}</div>`
              const separator = '<div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255,255,255,0.3);">'
              const separatorEnd = '</div>'
              
              if (games.length === 0) {
                return `<div style="padding: 4px;">${titleDate}${separator}无赛事${separatorEnd}</div>`
              } else {
                // 在 formatter 内部定义获取难度信息的函数
                const getDifficultyInfoLocal = (percentage) => {
                  if (percentage === null || percentage === undefined) {
                    return { level: 'undefined', label: '未定义', percentageText: '' }
                  }
                  const p = Number(percentage)
                  if (isNaN(p)) {
                    return { level: 'undefined', label: '未定义', percentageText: '' }
                  }
                  const percentageText = `${p.toFixed(2)}%`
                  if (p <= 14.29) return { level: 'red', label: '入门', percentageText }
                  if (p <= 28.58) return { level: 'orange', label: '普及-', percentageText }
                  if (p <= 42.87) return { level: 'yellow', label: '普及/提高-', percentageText }
                  if (p <= 57.15) return { level: 'green', label: '普及+/提高', percentageText }
                  if (p <= 71.44) return { level: 'blue', label: '提高+/省选-', percentageText }
                  if (p <= 85.72) return { level: 'purple', label: '省选/NOI-', percentageText }
                  return { level: 'black', label: 'NOI/NOI+/CTSC', percentageText }
                }
                
                // 获取难度标签样式（适度对比度，与热力图颜色保持一致）
                const getDifficultyTagStyle = (percentage) => {
                  if (percentage === null || percentage === undefined) {
                    return 'display: inline-block; padding: 0px 4px; margin-right: 4px; border-radius: 2px; font-size: 11px; background-color: rgba(184, 188, 200, 0.2); border: 1px solid #B8BCC8; color: #d0d4e0; line-height: 1.2;'
                  }
                  const difficultyInfo = getDifficultyInfoLocal(percentage)
                  const colorMap = {
                    red: { bg: 'rgba(255, 153, 153, 0.2)', border: '#FF9999', color: '#ffb3b3' },
                    orange: { bg: 'rgba(255, 179, 102, 0.2)', border: '#FFB366', color: '#ffcc99' },
                    yellow: { bg: 'rgba(255, 217, 102, 0.2)', border: '#FFD966', color: '#ffe699' },
                    green: { bg: 'rgba(168, 213, 168, 0.2)', border: '#A8D5A8', color: '#c4e6c4' },
                    blue: { bg: 'rgba(153, 204, 255, 0.2)', border: '#99CCFF', color: '#b3d9ff' },
                    purple: { bg: 'rgba(204, 153, 255, 0.2)', border: '#CC99FF', color: '#d9b3ff' },
                    black: { bg: 'rgba(102, 102, 153, 0.2)', border: '#666699', color: '#9999cc' },
                    undefined: { bg: 'rgba(184, 188, 200, 0.2)', border: '#B8BCC8', color: '#d0d4e0' }
                  }
                  const style = colorMap[difficultyInfo.level] || colorMap.undefined
                  return `display: inline-block; padding: 0px 4px; margin-right: 4px; border-radius: 2px; font-size: 11px; background-color: ${style.bg}; border: 1px solid ${style.border}; color: ${style.color}; line-height: 1.2;`
                }
                
                // 格式化每个赛事：🏆 赛事级别标签 难度标签(含百分比) 赛事名称 得分/订正得分（带颜色）
                const gameItems = games.map(game => {
                  const gameLevel = game.game_level || ''
                  const gameName = game.game_name || '未命名赛事'
                  const totalScore = game.total_score || 0
                  const totalScoreFix = game.total_score_fix || 0
                  const questionNum = game.question_num || 0
                  const gameAvgDifficulty = game.avg_difficulty ?? null  // 使用每个赛事自己的难度
                  
                  // 计算得分率（每题100分）
                  const maxScore = questionNum * 100
                  const scoreRate = maxScore > 0 ? (totalScore / maxScore) * 100 : 0
                  const scoreFixRate = maxScore > 0 ? (totalScoreFix / maxScore) * 100 : 0
                  
                  // 获取分数颜色
                  const scoreColor = getScoreColorByRate(scoreRate)
                  const scoreFixColor = getScoreColorByRate(scoreFixRate)
                  
                  // 构建显示文本
                  const parts = []
                  
                  // 赛事级别标签（如果有）
                  if (gameLevel) {
                    const tagStyle = getGameLevelTagStyle(gameLevel)
                    parts.push(`<span style="${tagStyle}">${gameLevel}</span>`)
                  }
                  
                  // 难度标签（在赛事级别和赛事名称之间），显示难度级别和百分比
                  const difficultyInfo = getDifficultyInfoLocal(gameAvgDifficulty)
                  const difficultyTagStyle = getDifficultyTagStyle(gameAvgDifficulty)
                  const difficultyText = difficultyInfo.percentageText 
                    ? `${difficultyInfo.label} (${difficultyInfo.percentageText})`
                    : difficultyInfo.label
                  parts.push(`<span style="${difficultyTagStyle}">${difficultyText}</span>`)
                  
                  // 赛事名称
                  parts.push(gameName)
                  
                  // 分数显示：得分/订正得分（带颜色）
                  if (totalScore > 0 || totalScoreFix > 0) {
                    // 总是显示得分/订正得分格式
                    const scoreText = `<span style="color: ${scoreColor}">${totalScore}</span>/<span style="color: ${scoreFixColor}">${totalScoreFix}</span>`
                    parts.push(scoreText)
                  }
                  
                  return `🏆 ${parts.join(' ')}`
                })
                
                return `<div style="padding: 4px;">${titleDate}${separator}${gameItems.join('<br/>')}${separatorEnd}</div>`
              }
            }
            return ''
          }
        },
        legend: {
          show: true,
          selectedMode: false,
          orient: 'horizontal',
          left: 'center',
          bottom: '7%',
          itemGap: 8,
          data: [{
            name: '✔️ 已订正',
            icon: 'circle'
        }],
          formatter: function(name) {
            return name
          },
          textStyle: {
            color: '#666',
            fontSize: 12
          }
        },
        // grid 配置由 updatePlanChartGrid 动态设置，不在这里硬编码
        xAxis: {
          type: 'category',
          data: xAxisData,
          splitArea: {
            show: false
          },
          axisLabel: {
            color: '#666',
            fontSize: 11,
            interval: 0,
            formatter: function(value, index) {
              return value
            }
          },
          axisTick: {
            show: false
          },
          axisLine: {
            show: false
          }
        },
        yAxis: {
          type: 'category',
          data: yAxisData,
          splitArea: {
            show: false
          },
          axisLabel: {
            color: '#666',
            fontSize: 12,
            margin: 8
          },
          axisTick: {
            show: false
          },
          axisLine: {
            show: false
          }
        },
        visualMap: {
          show: false,
          type: 'piecewise',
          selectedMode: false,
          orient: 'horizontal',
          left: 'center',
          bottom: '2%',
          itemWidth: 20,
          itemHeight: 14,
          dimension: this.heatmapMode === 'difficulty' ? 7 : 2,  // 根据模式选择维度
          pieces: this.heatmapMode === 'difficulty' ? [
            { value: -2, color: colorPalette.future, label: '时间未到' },
            { value: -1, color: colorPalette.noGame, label: '无赛事' },
            { value: -999, color: difficultyColorMap.undefined, label: '未定义' },  // 使用特殊值表示 null
            { min: 0, max: 14.29, color: difficultyColorMap.red, label: '入门' },
            { min: 14.29, max: 28.58, color: difficultyColorMap.orange, label: '普及-' },
            { min: 28.58, max: 42.87, color: difficultyColorMap.yellow, label: '普及/提高-' },
            { min: 42.87, max: 57.15, color: difficultyColorMap.green, label: '普及+/提高' },
            { min: 57.15, max: 71.44, color: difficultyColorMap.blue, label: '提高+/省选-' },
            { min: 71.44, max: 85.72, color: difficultyColorMap.purple, label: '省选/NOI-' },
            { min: 85.72, max: 100.01, color: difficultyColorMap.black, label: 'NOI/NOI+/CTSC' }  // 使用 100.01 确保包含 100
          ] : [
            { value: 0, color: countColorPalette.zero, label: '0' },
            { value: 1, color: countColorPalette.one, label: '1' },
            { value: 2, color: countColorPalette.two, label: '2' },
            { gt: 2, color: countColorPalette.threePlus, label: '≥3' }
          ],
          textStyle: {
            color: '#666',
            fontSize: 11
          }
        },
        series: [{
          name: '赛事数量',
          type: 'heatmap',
          progressive: 0,
          data: (this.chartData.plan.matrix || []).map(item => {
            if (Array.isArray(item) && item.length >= 3) {
              let weekIndex = Number(item[0]) || 0
              const dayOfWeek = Number(item[1]) || 0
              const count = Number(item[2]) || 0
              const dateStr = item[3] || ''
              const isFuture = Number(item[4]) === 1
              const games = item[5] || []  // 赛事详细信息列表
              const isAllCorrected = item[6] === true  // 是否所有赛事都已订正完毕
              const avgDifficulty = item[7] ?? null  // 平均难度百分比

              if (weekIndex >= weeksInYear) {
                console.warn('weekIndex超出范围:', weekIndex, 'weeksInYear:', weeksInYear)
                weekIndex = Math.max(0, Math.min(weekIndex, weeksInYear - 1))
              }

              const validDayOfWeek = Math.max(0, Math.min(dayOfWeek, 6))
              
              // 计算用于 visualMap 的值（根据模式选择）
              let visualMapValue
              if (this.heatmapMode === 'difficulty') {
                if (isFuture) {
                  visualMapValue = -2  // 时间未到
                } else if (count === 0) {
                  visualMapValue = -1  // 无赛事
                } else if (avgDifficulty === null || avgDifficulty === undefined) {
                  visualMapValue = -999  // 未定义（使用特殊值表示 null）
                } else {
                  visualMapValue = avgDifficulty  // 难度百分比
                }
              } else {
                // 数量模式：直接使用 count
                visualMapValue = count
              }
              
              const color = getCellColor(avgDifficulty, count, isFuture)
              const isGoldGlow = highlightGoldDateSet.has(dateStr)
              const baseItemStyle = {
                color,
                borderColor: '#ffffff',
                borderWidth: 4,
                borderRadius: 4,
                shadowBlur: 0,
                shadowColor: 'transparent',
                shadowOffsetX: 0,
                shadowOffsetY: 0
              }
              
              // 收集时间未到的单元格数据，用于绘制斜纹
              if (isFuture) {
                futureStripeData.push([weekIndex, validDayOfWeek])
              }
              
              if (isGoldGlow) {
                goldHighlightData.push([weekIndex, validDayOfWeek])
              }

              const itemStyle = baseItemStyle

              return {
                value: [weekIndex, validDayOfWeek, count, dateStr, isFuture ? 1 : 0, games, isAllCorrected, visualMapValue],
                itemStyle,
                // 如果所有赛事都已订正完毕，显示"✔️"图标
                label: isAllCorrected && count > 0 ? {
                  show: true,
                  formatter: '✔️',
                  fontSize: 12,  // 设置较小的字体大小，确保不会撑大单元格
                  color: '#fff',
                  fontWeight: 'normal',
                  position: 'inside'
                } : {
                  show: false
                }
              }
            }
            return {
              value: [0, 0, 0, '', 0, [], false, -1],
              itemStyle: {
                color: colorPalette.noGame,
                borderColor: '#ffffff',
                borderWidth: 2,
                borderRadius: 4
              },
              label: {
                show: false
              }
            }
          }).filter(item => {
            return item.value[0] >= 0 && item.value[0] < weeksInYear && item.value[1] >= 0 && item.value[1] < 7
          }),
          label: {
            show: false
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }, {
          type: 'custom',
          name: '',
          coordinateSystem: 'cartesian2d',
          data: goldHighlightData,
          silent: true,
          zlevel: 30,
          tooltip: { show: false },
          legendHoverLink: false,
          renderItem: (params, api) => {
            const coord = api.coord([api.value(0), api.value(1)])
            const size = api.size([1, 1])
            const rectShape = {
              x: coord[0] - size[0] / 2,
              y: coord[1] - size[1] / 2,
              width: size[0],
              height: size[1],
              r: 4
            }
            return {
              type: 'rect',
              shape: rectShape,
              style: {
                fill: 'transparent',
                stroke: goldGlowItemStyle.borderColor,
                lineWidth: goldGlowItemStyle.borderWidth,
                shadowBlur: goldGlowItemStyle.shadowBlur,
                shadowColor: goldGlowItemStyle.shadowColor,
                shadowOffsetX: goldGlowItemStyle.shadowOffsetX,
                shadowOffsetY: goldGlowItemStyle.shadowOffsetY
              },
              keyframeAnimation: {
                duration: 1700,
                loop: true,
                keyframes: [{
                  percent: 0,
                  style: {
                    stroke: 'rgba(253, 214, 121, 0.55)',
                    shadowColor: 'rgba(253, 214, 121, 0.45)',
                    shadowBlur: goldGlowItemStyle.shadowBlur * 0.5,
                    lineWidth: goldGlowItemStyle.borderWidth - 1
                  }
                }, {
                  percent: 0.5,
                  style: {
                    stroke: 'rgba(255, 230, 165, 1)',
                    shadowColor: 'rgba(255, 230, 165, 0.95)',
                    shadowBlur: goldGlowItemStyle.shadowBlur * 1.3,
                    lineWidth: goldGlowItemStyle.borderWidth + 1
                  }
                }, {
                  percent: 1,
                  style: {
                    stroke: 'rgba(253, 214, 121, 0.55)',
                    shadowColor: 'rgba(253, 214, 121, 0.45)',
                    shadowBlur: goldGlowItemStyle.shadowBlur * 0.5,
                    lineWidth: goldGlowItemStyle.borderWidth - 1
                  }
                }]
              }
            }
          }
        }, {
          type: 'custom',
          name: '',
          coordinateSystem: 'cartesian2d',
          data: futureStripeData,
          silent: true,
          zlevel: 25,
          tooltip: { show: false },
          legendHoverLink: false,
          renderItem: (params, api) => {
            const coord = api.coord([api.value(0), api.value(1)])
            const size = api.size([1, 1])
            const x = coord[0] - size[0] / 2
            const y = coord[1] - size[1] / 2
            const width = size[0]
            const height = size[1]
            
            // 绘制斜纹线条（从左上到右下）
            const stripeSpacing = 3
            const lines = []
            const diagonal = Math.sqrt(width * width + height * height)
            const angle = Math.atan2(height, width)
            
            // 计算需要绘制的斜线数量
            const numLines = Math.ceil(diagonal / stripeSpacing) + 2
            
            for (let i = -numLines; i <= numLines; i++) {
              const offset = i * stripeSpacing
              // 计算垂直于对角线的偏移
              const perpOffsetX = offset * Math.cos(angle + Math.PI / 2)
              const perpOffsetY = offset * Math.sin(angle + Math.PI / 2)
              
              // 从矩形外开始，穿过整个矩形
              const x1 = x - diagonal + perpOffsetX
              const y1 = y - diagonal + perpOffsetY
              const x2 = x + width + diagonal + perpOffsetX
              const y2 = y + height + diagonal + perpOffsetY
              
              lines.push({
                type: 'line',
                shape: {
                  x1: x1,
                  y1: y1,
                  x2: x2,
                  y2: y2
                },
                style: {
                  stroke: 'rgba(0, 0, 0, 0.15)',
                  lineWidth: 1
                }
              })
            }
            
            return {
              type: 'group',
              children: lines,
              clipPath: {
                type: 'rect',
                shape: {
                  x: x,
                  y: y,
                  width: width,
                  height: height
                }
              }
            }
          }
        }]
      }

      // 确保图表已初始化
      if (!this.planChart) {
        if (this.$refs.planChart) {
          try {
            this.planChart = echarts.init(this.$refs.planChart)
            console.log('热力图初始化成功，容器高度:', this.$refs.planChart.offsetHeight)
          } catch (error) {
            console.error('热力图初始化失败:', error)
            return
          }
        } else {
          console.error('planChart ref 不存在')
          return
        }
      }
      
      if (this.planChart && this.$refs.planChart) {
        const container = this.$refs.planChart
        
        // 先设置选项，使用 notMerge: true 确保完全替换
        try {
          this.planChart.setOption(planOption, { notMerge: true })
          const dataCount = planOption.series[0].data.length
          const validDataCount = planOption.series[0].data.filter(d => d[2] > 0).length
          console.log('热力图选项设置成功，数据点数量:', dataCount, '有效数据:', validDataCount)
          console.log('xAxisData长度:', planOption.xAxis.data.length, 'yAxisData长度:', planOption.yAxis.data.length)
          console.log('数据范围示例:', {
            weekIndex: planOption.series[0].data.slice(0, 5).map(d => d[0]),
            dayOfWeek: planOption.series[0].data.slice(0, 5).map(d => d[1]),
            values: planOption.series[0].data.slice(0, 5).map(d => d[2])
          })
        } catch (error) {
          console.error('设置热力图选项失败:', error)
        }
        
        // 确保容器有高度后再调整
        const updateChart = () => {
          if (!this.planChart || !container) return
          
          const height = container.offsetHeight || container.clientHeight
          const width = container.offsetWidth || container.clientWidth
          
          console.log('更新热力图，容器尺寸:', { height, width })
          
          if (height > 0 && width > 0) {
            // 容器有尺寸，执行resize和grid更新
            this.planChart.resize()
            this.updatePlanChartGrid()
            console.log('热力图更新完成')
          } else {
            // 容器尺寸为0，延迟重试
            console.warn('容器尺寸为0，延迟重试，当前尺寸:', { height, width })
            setTimeout(() => {
              updateChart()
            }, 200)
          }
        }
        
        // 使用 $nextTick 确保 DOM 更新完成
        this.$nextTick(() => {
          updateChart()
        })
      } else {
        console.error('planChart 初始化失败:', {
          planChart: this.planChart,
          ref: this.$refs.planChart
        })
      }
    },
    handleExportCorrection() {
      const dataURL = this.correctionChart.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff'
      })
      
      const link = document.createElement('a')
      link.download = '数据概览.png'
      link.href = dataURL
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    handleExportPlan() {
      const exportWrapper = this.$refs.planChartExportWrapper
      
      if (exportWrapper) {
        html2canvas(exportWrapper, {
          backgroundColor: '#fff',
          scale: window.devicePixelRatio || 2,
          useCORS: true
        }).then(canvas => {
          const link = document.createElement('a')
          link.download = '全年赛事图.png'
          link.href = canvas.toDataURL('image/png')
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }).catch(err => {
          console.error('导出全年赛事图失败:', err)
          this.$message.error('导出图片失败，请稍后重试')
        })
      } else if (this.planChart) {
        const dataURL = this.planChart.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff'
        })
        
        const link = document.createElement('a')
        link.download = '全年赛事图.png'
        link.href = dataURL
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else {
        console.warn('导出失败：planChart 实例不存在')
      }
    },
    handleExportRadar() {
      const dataURL = this.radarChart.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff'
      })
      
      const link = document.createElement('a')
      link.download = '能力雷达.png'
      link.href = dataURL
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    handleExportPlanOverview() {
      const dataURL = this.planOverviewChart.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff'
      })
      
      const link = document.createElement('a')
      link.download = '计划概览.png'
      link.href = dataURL
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    updatePlanChartGrid(targetChart = this.planChart, targetContainer = this.$refs.planChart) {
      // 确保图表实例和容器都存在
      if (!targetChart || !targetContainer) {
        console.warn('updatePlanChartGrid: 图表实例或容器不存在')
        return
      }
      
      // 计算图表容器的实际尺寸
      const chartContainer = targetContainer
      const containerWidth = chartContainer.offsetWidth || chartContainer.clientWidth
      const containerHeight = chartContainer.offsetHeight || chartContainer.clientHeight
      const styles = window.getComputedStyle(chartContainer)
      const paddingLeft = parseFloat(styles.paddingLeft || 0)
      const paddingRight = parseFloat(styles.paddingRight || 0)
      const paddingTop = parseFloat(styles.paddingTop || 0)
      const paddingBottom = parseFloat(styles.paddingBottom || 0)
      const innerWidth = Math.max(0, containerWidth - paddingLeft - paddingRight)
      const innerHeight = Math.max(0, containerHeight - paddingTop - paddingBottom)
      
      // 如果容器尺寸无效，不更新配置
      if (!innerWidth || !innerHeight || innerWidth <= 0 || innerHeight <= 0) {
        console.warn('updatePlanChartGrid: 容器尺寸无效', { containerWidth, containerHeight, innerWidth, innerHeight })
        return
      }
      
      // 计算数据比例：X轴是周数，Y轴是7天（优先使用后端元数据）
      const isLeapYear = (this.selectedYear % 4 === 0 && this.selectedYear % 100 !== 0) || (this.selectedYear % 400 === 0)
      const fallbackDays = isLeapYear ? 366 : 365
      const daysInYear = this.planDailyMeta.daysInYear || fallbackDays
      const weeksInYear = this.planDailyMeta.weeksInYear || Math.ceil(daysInYear / 7)
      const dataRatio = weeksInYear / 7 // 数据宽高比
      
      console.log('updatePlanChartGrid: 更新grid配置', {
        containerWidth,
        containerHeight,
        innerWidth,
        innerHeight,
        weeksInYear,
        daysInYear,
        dataRatio,
        target: targetChart === this.planChart ? 'main' : 'fullscreen'
      })
      
      // 判断是否是全屏对话框
      const isFullscreen = targetChart !== this.planChart
      
      // ========== 简单逻辑：设置预留空间 ==========
      let reservedTop, reservedBottom, reservedLeft, reservedRight
      
      if (isFullscreen) {
        // 全屏：上下左右各预留一些空间
        reservedTop = 10
        reservedBottom = innerHeight > 300 ? (innerHeight - 300) + 60 : 60  // 需要给图例、visualMap留出空间
        reservedLeft = 20
        reservedRight = 20
      } else {
        // 主视图：顶部10px，底部40px（给图例+visualMap留出有限空间，图例区域可通过负margin叠放），左右20px
        reservedTop = 10
        reservedBottom = 40
        reservedLeft = 20
        reservedRight = 20
      }
      
      // ========== 计算可用区域 ==========
      const availableWidth = innerWidth - reservedLeft - reservedRight
      const availableHeight = innerHeight - reservedTop - reservedBottom
      
      if (availableWidth <= 0 || availableHeight <= 0) {
        console.warn('updatePlanChartGrid: 可用空间无效', { availableWidth, availableHeight })
        return
      }
      
      // ========== 计算 cellSize（确保正方形） ==========
      // 每个小方格的宽度 = 可用宽度 / 周数
      // 每个小方格的高度 = 可用高度 / 7
      // 取最小值，确保是正方形
      const cellWidth = availableWidth / weeksInYear
      const cellHeight = availableHeight / 7
      const cellSize = Math.min(cellWidth, cellHeight)
      
      // ========== 计算实际的 grid 尺寸 ==========
      const gridWidth = cellSize * weeksInYear
      const gridHeight = cellSize * 7
      
      // ========== 关键修复：确保 grid 的实际可用空间 = gridWidth x gridHeight ==========
      // 计算 grid 的 left/right/top/bottom，使得实际可用空间正好等于 gridWidth x gridHeight
      // 这样 ECharts 就不会自动调整 cellSize
      
      // 水平居中：确保实际可用宽度 = gridWidth
      const leftSpace = paddingLeft + reservedLeft + (availableWidth - gridWidth) / 2
      const rightSpace = paddingRight + reservedRight + (availableWidth - gridWidth) / 2
      
      // 垂直位置：确保实际可用高度 = gridHeight（关键！）
      // 如果 cellSize 是基于 cellWidth 计算的，那么 gridHeight < availableHeight
      // 我们需要让 grid 的实际可用高度 = gridHeight，而不是 availableHeight
      let topSpace, bottomSpace
      if (!isFullscreen) {
        // 主视图：靠下显示，向下移动 10px，减小与图例的间距
        // 顶部增加 10px（向下移动）
        topSpace = paddingTop + reservedTop + 10
        // 关键：bottomSpace 要确保 actualGridHeight = gridHeight
        // actualGridHeight = innerHeight - topSpace - bottomSpace = gridHeight
        // 所以：bottomSpace = innerHeight - topSpace - gridHeight
        // 由于 topSpace 增加了 10px，bottomSpace 会自动减少 10px，减小与图例的间距
        bottomSpace = innerHeight - topSpace - gridHeight
      } else {
        // 全屏：居中显示
        // 关键：确保 actualGridHeight = gridHeight
        const remainingHeight = innerHeight - paddingTop - paddingBottom - gridHeight
        topSpace = paddingTop + remainingHeight / 2
        bottomSpace = paddingBottom + remainingHeight / 2
      }
      
      // ========== 应用配置 ==========
      try {
        const currentOption = targetChart.getOption()
        const seriesOption = currentOption.series && currentOption.series[0] ? { ...currentOption.series[0] } : {}
        
        // 关键：使用数字格式 cellSize，ECharts 会自动识别为正方形
        // 同时确保 grid 的实际可用空间 = gridWidth x gridHeight
        seriesOption.cellSize = cellSize
        
        targetChart.setOption({
          grid: {
            top: `${topSpace}px`,
            bottom: `${bottomSpace}px`,
            left: `${leftSpace}px`,
            right: `${rightSpace}px`,
            containLabel: true
          },
          series: [seriesOption]
        }, { 
          notMerge: false
        })
        
        // 验证：计算 grid 的实际可用空间
        const actualGridWidth = innerWidth - leftSpace - rightSpace
        const actualGridHeight = innerHeight - topSpace - bottomSpace
        
        console.log('updatePlanChartGrid: 配置已更新', {
          innerWidth,
          innerHeight,
          availableWidth,
          availableHeight,
          cellWidth,
          cellHeight,
          cellSize,
          gridWidth,
          gridHeight,
          topSpace,
          bottomSpace,
          leftSpace,
          rightSpace,
          actualGridWidth,
          actualGridHeight,
          '关键验证': {
            'cellSize计算': cellSize,
            'gridWidth计算': gridWidth,
            'gridHeight计算': gridHeight,
            '实际可用宽度': actualGridWidth,
            '实际可用高度': actualGridHeight,
            '宽度匹配': Math.abs(actualGridWidth - gridWidth) < 0.1,
            '高度匹配': Math.abs(actualGridHeight - gridHeight) < 0.1,
            'cellSize是否正方形': Math.abs((gridWidth / weeksInYear) - (gridHeight / 7)) < 0.01,
            '每个cell应该是': {
              '宽度': gridWidth / weeksInYear,
              '高度': gridHeight / 7,
              '是否相等': Math.abs((gridWidth / weeksInYear) - (gridHeight / 7)) < 0.01
            }
          }
        })
      } catch (error) {
        console.error('更新 grid 配置失败:', error)
      }
    },
    handleDialogOpen() {
      if (this.isInitializing) return
      this.isInitializing = true

      // 确保在对话框完全打开后初始化图表
      this.$nextTick(() => {
        try {
          const chartDom = this.$refs.fullscreenChart
          if (!chartDom || !this.currentChartType) return

          // 初始化新的图表实例
          this.fullscreenChart = echarts.init(chartDom)
          let option = null

          // 根据类型获取对应的图表配置
          switch (this.currentChartType) {
            case 'correction':
              option = this.correctionChart.getOption()
              break
            case 'planOverview':
              option = this.planOverviewChart.getOption()
              break
            case 'plan':
              option = this.planChart.getOption()
              break
            case 'radar':
              option = this.radarChart.getOption()
              option.radar.radius = '60%'
              option.radar.center = ['50%', '45%']
              break
          }

          if (option) {
            this.fullscreenChart.setOption(option)

            if (this.currentChartType === 'plan') {
              // 等待图表渲染完成后再调整grid
              this.$nextTick(() => {
                // 确保容器有尺寸后再调整
                const updateGrid = () => {
                  if (!this.fullscreenChart || !chartDom) return
                  const height = chartDom.offsetHeight || chartDom.clientHeight
                  const width = chartDom.offsetWidth || chartDom.clientWidth
                  if (height > 0 && width > 0) {
                    this.fullscreenChart.resize()
                    this.updatePlanChartGrid(this.fullscreenChart, chartDom)
                  } else {
                    setTimeout(updateGrid, 100)
                  }
                }
                updateGrid()
              })
            }
            
            // 创建新的 resize 处理函数
            this.resizeHandler = _.debounce(() => {
              if (this.fullscreenChart) {
                this.fullscreenChart.resize()
                if (this.currentChartType === 'plan') {
                  this.$nextTick(() => {
                    this.updatePlanChartGrid(this.fullscreenChart, chartDom)
                  })
                }
              }
            }, 100)

            // 添加 resize 事件监听
            window.addEventListener('resize', this.resizeHandler)
          }
        } catch (error) {
          console.error('初始化全屏图表失败:', error)
          this.$message.error('初始化图表失败')
        } finally {
          this.isInitializing = false
        }
      })
    },
    handleDialogClose() {
      // 在关闭前清理
      this.cleanupFullscreen()
    },
    handleDialogClosed() {
      // 在完全关闭后重置状态
      this.currentChartType = ''
      this.isInitializing = false
    },
    cleanupFullscreen() {
      // 移除 resize 事件监听
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }

      // 销毁图表实例
      if (this.fullscreenChart) {
        this.fullscreenChart.dispose()
        this.fullscreenChart = null
      }
    },
    handleFullscreenCorrection() {
      // 先清理旧的实例
      this.cleanupFullscreen()
      this.currentChartType = 'correction'
      this.dialogTitle = '数据概览'
      this.dialogVisible = true
    },
    handleFullscreenPlanOverview() {
      this.cleanupFullscreen()
      this.currentChartType = 'planOverview'
      this.dialogTitle = '计划概览'
      this.dialogVisible = true
    },
    handleFullscreenPlan() {
      this.cleanupFullscreen()
      this.currentChartType = 'plan'
      this.dialogTitle = '全年赛事图'
      this.dialogVisible = true
    },
    handleFullscreenRadar() {
      this.cleanupFullscreen()
      this.currentChartType = 'radar'
      this.dialogTitle = '能力雷达'
      this.dialogVisible = true
    },
    closeDialog() {
      this.dialogVisible = false
    },
    async handleExportFullscreen() {
      try {
        // 如果是全年赛事图，使用html2canvas导出完整内容（包括进度条、数据卡片和热力图）
        if (this.currentChartType === 'plan' && this.$refs.fullscreenPlanWrapper) {
          const wrapper = this.$refs.fullscreenPlanWrapper
          const canvas = await html2canvas(wrapper, {
            backgroundColor: '#fff',
            scale: 2,
            useCORS: true,
            logging: false
          })
          
          const dataURL = canvas.toDataURL('image/png')
          const link = document.createElement('a')
          link.download = `${this.dialogTitle}.png`
          link.href = dataURL
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        } else if (this.fullscreenChart) {
          // 其他图表使用ECharts的getDataURL方法
          const dataURL = this.fullscreenChart.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#fff'
          })
          
          const link = document.createElement('a')
          link.download = `${this.dialogTitle}.png`
          link.href = dataURL
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      } catch (error) {
        console.error('导出图片失败:', error)
        this.$message.error('导出图片失败')
      }
    },
    handleYearChange() {
      this.fetchData()
    }
  }
}
</script>

<style scoped>
.study-effectiveness {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.study-effectiveness .chart-row {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.study-effectiveness .chart-row.full-width {
  width: 100%;
}

.chart-card {
  flex: 1;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

.full-width-card {
  width: 100%;
  flex: none;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-radius: 8px 8px 0 0;
  flex-shrink: 0;
}

.chart-card :deep(.el-card__header) {
  padding: 0;
  border-bottom: none;
}

.chart-container {
  height: 300px;
  width: 100%;
  box-sizing: border-box;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: visible; /* 确保图例不被裁剪 */
}

.chart-card:first-child .chart-container {
  height: 220px; /* 数据概览图表区域高度，确保整体卡片高度为440px (50px header + 170px data + 220px chart) */
  flex-shrink: 0; /* 防止被压缩 */
}

.chart-row .chart-card:nth-child(2) .chart-container {
  height: 220px; /* 计划概览图表高度，确保整体卡片高度为440px (50px header + 170px data + 220px chart) */
  flex-shrink: 0; /* 防止被压缩 */
}

.chart-row .chart-card:nth-child(2) .data-statistics {
  height: 170px; /* 计划概览统计数据区域固定高度，确保2行数据正常显示 (60px * 2 + 12px gap + 24px padding + 14px margin) */
  min-height: 170px;
  max-height: 170px;
  flex-shrink: 0; /* 防止被压缩 */
}

.radar-chart-container {
  height: 390px; /* 能力雷达图表高度，确保整体卡片高度为440px (50px header + 390px chart) */
  padding: 0 20px 12px;
  box-sizing: border-box;
}

.data-statistics {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 20px;
  box-sizing: border-box;
  flex-shrink: 0; /* 防止被压缩 */
  overflow: visible; /* 确保内容不被裁剪 */
}

.chart-card:first-child .data-statistics {
  height: 170px; /* 数据概览统计数据区域固定高度，确保2行数据正常显示 (60px * 2 + 12px gap + 24px padding + 14px margin) */
  min-height: 170px;
  max-height: 170px;
  flex-shrink: 0; /* 防止被压缩 */
}

.statistic-row {
  display: flex;
  justify-content: space-between;
  gap: 20px;
}

.statistic-item {
  flex: 1;
  padding: 8px 20px;
  background: #F8F9FC;
  border-radius: 8px;
  text-align: center;
  transition: all 0.3s ease;
  height: 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-sizing: border-box;
}

.statistic-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.statistic-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 4px;
}

.statistic-value {
  font-size: 24px;
  font-weight: bold;
  color: #333;
  line-height: 1.2;
}

.statistic-value .highlight {
  color: #409EFF;
}

.statistic-value .separator {
  margin: 0 4px;
  font-weight: normal;
  color: #999;
  font-size: 20px;
}

.operations {
  display: flex;
  gap: 8px;
  align-items: center;
}

.operations .el-button {
  color: #ffffff;
}

.operations .el-button:hover {
  color: #BD6C73;
}

.heatmap-mode-switch {
  margin-right: 8px;
}

.heatmap-mode-switch .el-button {
  color: #ffffff;
  background-color: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
}

.heatmap-mode-switch .el-button.is-active,
.heatmap-mode-switch .el-button--primary {
  background-color: #ffffff;
  border-color: #ffffff;
  color: #2E365A;
}

.heatmap-mode-switch .el-button:hover {
  background-color: rgba(255, 255, 255, 0.3);
  color: #ffffff;
}

.el-button [class^="el-icon-"] {
  font-size: 16px;
}

.chart-dialog :deep(.el-dialog__header) {
  padding: 0;
  margin: 0;
}

.chart-dialog :deep(.el-dialog__headerbtn) {
  display: none;
}

.chart-dialog :deep(.el-dialog__body) {
  padding: 0;
  min-height: 490px;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-bottom: none;
}

.dialog-operations {
  display: flex;
  gap: 8px;
}

.dialog-operations .el-button {
  color: #ffffff;
}

.dialog-operations .el-button:hover {
  color: #BD6C73;
}

.fullscreen-chart {
  width: 100%;
  height: 520px;
  padding: 16px;
  box-sizing: border-box;
}

.fullscreen-plan-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 490px;
  box-sizing: border-box;
  background-color: #fff;
}

.fullscreen-progress-section {
  display: flex;
  gap: 30px;
  padding: 20px 30px 12px 30px;
  box-sizing: border-box;
  align-items: center;
  flex-shrink: 0;
  min-height: 100px;
  position: relative;
}

.fullscreen-progress-section .progress-divider {
  width: 1px;
  background-color: #EBEEF5;
  height: 80px;
  flex-shrink: 0;
  margin: 0 10px;
  align-self: center;
}

.fullscreen-statistics-cards {
  display: flex;
  gap: 20px;
  flex-shrink: 0;
}

.fullscreen-statistics-cards .stat-card {
  width: 220px;
  height: 76px;
}

.fullscreen-plan-wrapper .fullscreen-chart {
  height: 360px;
  flex-shrink: 0;
  padding: 10px 20px 20px;
  box-sizing: border-box;
  overflow: visible;
  position: relative;
  width: 100%;
  display: block;
}

.matrix-chart-export-wrapper {
  width: 100%;
  position: relative;
}

.matrix-chart-container {
  height: 272px; /* canvas高度保持272px（border-box包含padding） */
  min-height: 272px;
  max-height: 272px;
  padding: 10px 20px 20px; /* 顶部10px，左右20px，底部20px，总共30px */
  box-sizing: border-box;
  flex: 0 0 auto; /* 不参与flex扩展，保证高度固定 */
  overflow: visible; /* 确保图表可以显示 */
  position: relative; /* 确保定位正确 */
  width: 100%; /* 确保宽度100% */
  display: block; /* 确保是块级元素，不是 flex */
}

.matrix-legends {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin: -15px 0 12px;
  font-size: 12px;
  color: #606266;
  flex-wrap: wrap;
  position: relative;
  z-index: 2;
}

.matrix-legends.fullscreen {
  margin: -55px 0 0;
  flex-shrink: 0;
}

.matrix-legends .legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.matrix-legends .legend-square {
  width: 18px;
  height: 12px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.matrix-legends .legend-icon {
  font-size: 14px;
}

.full-width-card {
  height: 480px; /* 全年赛事图卡片高度： 480px */
  display: flex;
  flex-direction: column;
}

.full-width-card ::v-deep .el-card__body {
  display: flex;
  flex-direction: column;
  height: 430px; /* 480px - 50px header = 430px */
  padding: 0;
  box-sizing: border-box;
  overflow: visible; /* 确保图表可以显示 */
  position: relative; /* 确保定位正确 */
}

.year-progress-section {
  display: flex;
  gap: 30px;
  padding: 20px 30px 12px 30px; /* 增加底部padding，避免与图表重叠 */
  box-sizing: border-box;
  align-items: flex-start;
  flex-shrink: 0;
  min-height: 70px; /* 使用min-height而不是固定height */
}

.annual-progress {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.progress-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.progress-title {
  font-size: 16px;
  font-weight: 500;
  color: #303133;
}

.progress-bar-container {
  width: 100%;
  height: 24px;
  background-color: #f5f7fa;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #409EFF 0%, #67C23A 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 8px;
  transition: width 0.3s ease;
  position: relative;
}

.progress-value {
  font-size: 12px;
  font-weight: bold;
  color: #ffffff;
  white-space: nowrap;
}

.progress-labels {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #909399;
}

.progress-divider {
  width: 1px;
  background-color: #EBEEF5;
  height: 80%;
  align-self: center;
  flex-shrink: 0;
  margin: 0 10px;
}

.statistics-cards {
  display: flex;
  gap: 20px;
  flex-shrink: 0;
}

.stat-card {
  width: 150px;
  padding: 12px 16px; /* 减少padding，从20px减少到12px 16px */
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 12px; /* 减少gap，从16px减少到12px */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  height: 60px; /* 固定高度，减小卡片高度 */
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.stat-card-total {
  border-left: 4px solid #409EFF;
  background: linear-gradient(135deg, #f0f9ff 0%, #e6f4ff 100%);
}

.stat-card-year {
  border-left: 4px solid #67C23A;
  background: linear-gradient(135deg, #f0f9f0 0%, #e6f5e6 100%);
}

.stat-icon {
  width: 30px; /* 减小icon大小，从48px减少到30px */
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 28px; /* emoji图标大小 */
  line-height: 1;
}

.stat-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-number {
  font-size: 28px; /* 减小数字大小，从32px减少到28px */
  font-weight: bold;
  line-height: 1;
}

.stat-card-total .stat-number {
  color: #409EFF;
}

.stat-card-year .stat-number {
  color: #67C23A;
}

.stat-label {
  font-size: 14px;
  color: #666;
  line-height: 1.2;
}

.progress-item .year {
  font-size: 24px;
  font-weight: bold;
  color: #409EFF;
}

.progress-item .value {
  font-size: 24px;
  font-weight: bold;
  color: #409EFF;
}

.year-select {
  width: 100px;
  margin-right: 8px;
}

.year-select :deep(.el-input__inner) {
  height: 28px;
  line-height: 28px;
  background-color: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 4px;
  color: #fff;
  padding-right: 25px;
}

.year-select :deep(.el-input__inner:hover) {
  background-color: rgba(255, 255, 255, 0.2);
}

.year-select :deep(.el-input__suffix) {
  right: 5px;
}

.year-select :deep(.el-select__caret) {
  color: #fff;
  line-height: 28px;
}

.year-select :deep(.el-select__caret.is-reverse) {
  transform: rotateZ(180deg);
}
</style> 